/**
 * Credit context.
 */
import React from 'react'
import config from 'react-global-configuration'
import { apiFetchWithAccessToken, getItemsCountResponse } from './lib/api-fetch'

export const CreditContext = React.createContext(null)
export const CreditConsumer = CreditContext.Consumer

interface CreditStoreProps {
  children: any
}

export interface CreditContext {
  account: any
  loadingAccount: boolean
  transactions: any[]
  transactionsTotalCount: number
  transactionsPageCount: number
  transactionsLastKey: string
  invoices: any[]
  invoicesTotalCount: number
  invoicesPageCount: number
  invoicesLastKey: string
  allLicenses: any[]
  allLicensesTotalCount: number
  allLicensesPageCount: number
  serviceProductLicenses: any[]
  serviceProductLicensesTotalCount: number
  serviceProductLicensesPageCount: number
  serviceProducts: any[]
  serviceProductsTotalCount: number
  serviceProductsPageCount: number
  serviceProduct: any[]
  serviceProductPlans: any[]
  serviceProductPlansTotalCount: number
  serviceProductPlansPageCount: number
  newLicense: any
  usedVouchers: any[]
  usedVouchersTotalCount: number
  usedVouchersPageCount: number
  usedVouchersLastKey: string
  usedVoucher: any
  stripePaymentMethods: any[]
  fetchingStripePaymentMethods: boolean
  creatingPaymentIntent: boolean
  confirmingPaymentIntent: boolean

  fetchAccount: Function
  updateAccount: Function
  fetchTransactions: Function
  fetchInvoices: Function
  fetchAllLicenses: Function
  fetchServiceProductLicenses: Function
  fetchServiceProducts: Function
  fetchServiceProduct: Function
  fetchServiceProductPlans: Function
  createOrRenewLicense: Function
  reallocateLicense: Function
  fetchUsedVouchers: Function
  useVoucher: Function
  fetchStripePaymentMethods: Function
  attachStripePaymentMethod: Function
  detachStripePaymentMethod: Function
  createStripePaymentIntentForProduct: Function
  createStripePaymentIntentForCreditDeposit: Function
  confirmAndCaptureStripePaymentIntent: Function
  handleStripePaymentActions: Function
  fetchDiscount: Function
}

export class CreditStore extends React.Component<CreditStoreProps> {
  state = {
    account: null,
    loadingAccount: false,
    transactions: [],
    transactionsTotalCount: 0,
    transactionsPageCount: 0,
    transactionsLastKey: '',
    invoices: [],
    invoicesTotalCount: 0,
    invoicesPageCount: 0,
    invoicesLastKey: '',
    allLicenses: [],
    allLicensesTotalCount: 0,
    allLicensesPageCount: 0,
    serviceProductLicenses: [],
    serviceProductLicensesTotalCount: 0,
    serviceProductLicensesPageCount: 0,
    serviceProducts: [],
    serviceProductsTotalCount: 0,
    serviceProductsPageCount: 0,
    serviceProduct: [],
    serviceProductPlans: [],
    serviceProductPlansTotalCount: 0,
    serviceProductPlansPageCount: 0,
    newLicense: null,
    usedVouchers: [],
    usedVouchersTotalCount: 0,
    usedVouchersPageCount: 0,
    usedVouchersLastKey: '',
    usedVoucher: null,
    stripePaymentMethods: [],
    fetchingStripePaymentMethods: false,
    creatingPaymentIntent: false,
    confirmingPaymentIntent: false,
  }

  async componentDidMount() {
  }

  fetchAccount = async () => {
    this.setState({
      loadingAccount: true,
    })
    try {
      const response = await apiFetchWithAccessToken('GET', config.get('CREDITS_API_URL') + '/account')
      this.setState({
        account: response,
        loadingAccount: false,
      })
      return response
    } catch (err) {
      this.setState({
        loadingAccount: false,
      })
      throw err
    }
  }

  updateAccount = async (account: any) => {
    const response = await apiFetchWithAccessToken('PUT', config.get('CREDITS_API_URL') + '/account', account)
    this.setState({
      account: response,
    })
    return response
  }

  fetchTransactions = async (startKey: any) => {
    const fullResponse = await apiFetchWithAccessToken('GET', config.get('CREDITS_API_URL') + '/transactions' + (startKey ? '?start=' + startKey : ''), undefined, { fullResponse: true })
    const response = getItemsCountResponse(fullResponse)
    this.setState({
      transactions: response.items,
      transactionsTotalCount: response.totalCount,
      transactionsPageCount: response.pageCount,
      transactionsLastKey: response.lastKey,
    })
    return response
  }

  fetchInvoices = async (startKey: any) => {
    const fullResponse = await apiFetchWithAccessToken('GET', config.get('CREDITS_API_URL') + '/invoices' + (startKey ? '?start=' + startKey : ''), undefined, { fullResponse: true })
    const response = getItemsCountResponse(fullResponse)
    this.setState({
      invoices: response.items,
      invoicesTotalCount: response.totalCount,
      invoicesPageCount: response.pageCount,
      invoicesLastKey: response.lastKey,
    })
    return response
  }

  fetchAllLicenses = async (startKey: any) => {
    const fullResponse = await apiFetchWithAccessToken('GET', config.get('CREDITS_API_URL') + '/licenses' + (startKey ? '?start=' + startKey : ''), undefined, { fullResponse: true })
    const response = getItemsCountResponse(fullResponse)
    this.setState({
      allLicenses: response.items,
      allLicensesTotalCount: response.totalCount,
      allLicensesPageCount: response.pageCount,
    })
    return response
  }

  fetchServiceProductLicenses = async (serviceId: string, productId: string, startKey: any) => {
    const fullResponse = await apiFetchWithAccessToken('GET', config.get('CREDITS_API_URL') + '/services/' + serviceId + '/products/' + productId + '/licenses' + (startKey ? '?start=' + startKey : ''), undefined, { fullResponse: true })
    const response = getItemsCountResponse(fullResponse)
    this.setState({
      serviceProductLicenses: response.items,
      serviceProductLicensesTotalCount: response.totalCount,
      serviceProductLicensesPageCount: response.pageCount,
    })
    return response
  }

  fetchServiceProducts = async (serviceId: string, startKey: any) => {
    const fullResponse = await apiFetchWithAccessToken('GET', config.get('CREDITS_API_URL') + '/services/' + serviceId + '/products' + (startKey ? '?start=' + startKey : ''), undefined, { fullResponse: true })
    const response = getItemsCountResponse(fullResponse)
    this.setState({
      serviceProducts: response.items,
      serviceProductsTotalCount: response.totalCount,
      serviceProductsPageCount: response.pageCount,
    })
    return response
  }

  fetchServiceProduct = async (serviceId: string, productId: string) => {
    const response = await apiFetchWithAccessToken('GET', config.get('CREDITS_API_URL') + '/services/' + serviceId + '/products/' + productId)
    this.setState({
      serviceProduct: response,
    })
    return response
  }

  fetchServiceProductPlans = async (serviceId: string, productId: string, startKey: any) => {
    const fullResponse = await apiFetchWithAccessToken('GET', config.get('CREDITS_API_URL') + '/services/' + serviceId + '/products/' + productId + '/plans' + (startKey ? '?start=' + startKey : ''), undefined, { fullResponse: true })
    const response = getItemsCountResponse(fullResponse)
    response.items.sort((a: any, b: any) => a.price - b.price)
    this.setState({
      serviceProductPlans: response.items,
      serviceProductPlansTotalCount: response.totalCount,
      serviceProductPlansPageCount: response.pageCount,
    })
    return response
  }

  createOrRenewLicense = async (serviceId: string, productId: string, planId: string, allocationId: string, recurring: boolean, stripeAmount: string, stripeCurrency: string, stripeToken: string) => {
    const response = await apiFetchWithAccessToken('PUT', config.get('CREDITS_API_URL') + '/services/' + serviceId + '/products/' + productId + '/plans/' + planId + '/licenses/' + allocationId, {
      recurring: recurring,
      stripeAmount: stripeAmount || null,
      stripeCurrency: stripeCurrency || null,
      stripeToken: stripeToken || null,
    })
    this.setState({
      newLicense: response,
    })
    return response
  }

  reallocateLicense = async (serviceId: string, productId: string, planId: string, allocationId: string, licenseId: string, recurring: boolean, nextPlanId: string) => {
    const response = await apiFetchWithAccessToken('PUT', config.get('CREDITS_API_URL') + '/services/' + serviceId + '/products/' + productId + '/plans/' + planId + '/licenses/' + allocationId, {
      license: licenseId,
      recurring: recurring,
      nextPlanId: nextPlanId || undefined,
    })
    this.setState({
      newLicense: response,
    })
    return response
  }

  fetchUsedVouchers = async (page: any) => {
    const fullResponse = await apiFetchWithAccessToken('GET', config.get('CREDITS_API_URL') + '/vouchers' + (page ? '?page=' + page : ''), undefined, { fullResponse: true })
    const response = getItemsCountResponse(fullResponse)
    this.setState({
      usedVouchers: response.items,
      usedVouchersTotalCount: response.totalCount,
      usedVouchersPageCount: response.pageCount,
      usedVouchersLastKey: response.lastKey,
    })
    return response
  }

  useVoucher = async (voucherCode: string) => {
    const response = await apiFetchWithAccessToken('POST', config.get('CREDITS_API_URL') + '/vouchers', {
      code: voucherCode,
    })
    this.setState({
      usedVoucher: response,
    })
    return response
  }

  fetchDiscount = async (serviceId: string, productId: string, discountCode: string, variantId?: string) => {
    const response = await apiFetchWithAccessToken('POST', config.get('CREDITS_API_URL') + '/services/' + serviceId + '/products/' + productId + '/check-discount-code', {
      discountCode: discountCode,
      variantId: variantId,
    })
    return response
  }

  fetchStripePaymentMethods = async () => {
    this.setState({
      fetchingPaymentMethods: true,
    })
    let response
    try {
      response = await apiFetchWithAccessToken('GET', config.get('CREDITS_API_URL') + '/stripe/payment-methods')
    } catch (err) {
      this.setState({
        stripePaymentMethods: [],
        fetchingPaymentMethods: false,
      })
      throw err
    }
    this.setState({
      stripePaymentMethods: response,
      fetchingPaymentMethods: false,
    })
    return response
  }

  attachStripePaymentMethod = async (paymentMethodId: any) => {
    const response = await apiFetchWithAccessToken('PUT', config.get('CREDITS_API_URL') + '/stripe/payment-methods/' + paymentMethodId, {})
    return response
  }

  detachStripePaymentMethod = async (paymentMethodId: string) => {
    const response = await apiFetchWithAccessToken('DELETE', config.get('CREDITS_API_URL') + '/stripe/payment-methods/' + paymentMethodId)
    return response
  }

  createStripePaymentIntentForProduct = async (paymentMethodId: string, serviceId: string, productId: string, variantId?: string, discountCode?: string) => {
    this.setState({
      creatingPaymentIntent: true,
    })
    let response
    try {
      response = await apiFetchWithAccessToken('POST', config.get('CREDITS_API_URL') + '/stripe/payment-intents', {
        paymentMethodId: paymentMethodId,
        serviceId: serviceId,
        productId: productId,
        variantId: variantId,
        discountCode: discountCode,
      })
    } catch (err) {
      this.setState({
        creatingPaymentIntent: false,
      })
      throw err
    }
    this.setState({
      creatingPaymentIntent: false,
    })
    console.log('Create payment intent response:', response)
    return response
  }

  createStripePaymentIntentForCreditDeposit = async (paymentMethodId: string, amount: string, taxAmount: string, taxPercent: string, currency: string, description: string) => {
    this.setState({
      creatingPaymentIntent: true,
    })
    let response
    try {
      response = await apiFetchWithAccessToken('POST', config.get('CREDITS_API_URL') + '/stripe/payment-intents', {
        paymentMethodId: paymentMethodId,
        amount: amount,
        taxAmount: taxAmount,
        taxPercent: taxPercent,
        currency: currency,
        description: description,
      })
    } catch (err) {
      this.setState({
        creatingPaymentIntent: false,
      })
      throw err
    }
    this.setState({
      creatingPaymentIntent: false,
    })
    console.log('Create payment intent response:', response)
    return response
  }

  confirmAndCaptureStripePaymentIntent = async (paymentIntentId: string) => {
    this.setState({
      confirmingPaymentIntent: true,
    })
    let response
    try {
      response = await apiFetchWithAccessToken('POST', config.get('CREDITS_API_URL') + '/stripe/payment-intents/' + paymentIntentId + '/confirm-and-capture', {})
    } catch (err) {
      this.setState({
        confirmingPaymentIntent: false,
      })
      throw err
    }
    this.setState({
      confirmingPaymentIntent: false,
    })
    console.log('Confirm payment intent response:', response)
    return response
  }

  handleStripePaymentActions = async (stripe: any, paymentIntent: any) => {
    console.log('Checking if actions are required for payment intent', paymentIntent)
    if (paymentIntent.status === 'requires_action' || paymentIntent.status === 'requires_source_action') {
      // Action required
      console.log('Handling action', paymentIntent.next_action)
      await stripe.handleCardAction(paymentIntent.client_secret)
      return false
    }
    return true
  }

  render() {
    return (
      <CreditContext.Provider value={{ ...this.state,
        fetchAccount: this.fetchAccount,
        updateAccount: this.updateAccount,
        fetchTransactions: this.fetchTransactions,
        fetchInvoices: this.fetchInvoices,
        fetchAllLicenses: this.fetchAllLicenses,
        fetchServiceProductLicenses: this.fetchServiceProductLicenses,
        fetchServiceProducts: this.fetchServiceProducts,
        fetchServiceProduct: this.fetchServiceProduct,
        fetchServiceProductPlans: this.fetchServiceProductPlans,
        createOrRenewLicense: this.createOrRenewLicense,
        reallocateLicense: this.reallocateLicense,
        fetchUsedVouchers: this.fetchUsedVouchers,
        useVoucher: this.useVoucher,
        fetchStripePaymentMethods: this.fetchStripePaymentMethods,
        attachStripePaymentMethod: this.attachStripePaymentMethod,
        detachStripePaymentMethod: this.detachStripePaymentMethod,
        createStripePaymentIntentForProduct: this.createStripePaymentIntentForProduct,
        createStripePaymentIntentForCreditDeposit: this.createStripePaymentIntentForCreditDeposit,
        confirmAndCaptureStripePaymentIntent: this.confirmAndCaptureStripePaymentIntent,
        handleStripePaymentActions: this.handleStripePaymentActions,
        fetchDiscount: this.fetchDiscount,
      }}>
        {this.props.children}
      </CreditContext.Provider>
    )
  }
}

export function withCreditContext(Comp: any) {
  return (props: any) => (
    <CreditContext.Consumer>
      {(creditContext: CreditContext) => <Comp {...props} creditContext={{...creditContext}}/>}
    </CreditContext.Consumer>
  )
}
